import { useMemo } from "react";
import { useTheme } from "@mui/material";
import { StackedBarChartSeries } from "../types";

export const useChartOptions = (
  height: number,
  chartData: StackedBarChartSeries[] | undefined
): Highcharts.Options => {
  const theme = useTheme();

  const series = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return [];
    }

    return chartData.map((seriesItem) => ({
      name: seriesItem.name,
      data: seriesItem.data,
      type: "column" as const,
      color: seriesItem.color || theme.palette.primary.main,
      stack: seriesItem.stack,
    }));
  }, [chartData, theme.palette.primary.main]);

  return useMemo(() => {
    return {
      title: {
        text: "",
      },
      credits: {
        enabled: false,
      },
      chart: {
        type: "column",
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
        height,
      },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.overline.fontSize as string,
            fontWeight: "500",
          },
        },
        lineColor: theme.palette.divider,
        tickColor: theme.palette.divider,
      },
      yAxis: {
        title: {
          text: "",
        },
        allowDecimals: false,
        min: 0,
        labels: {
          enabled: true,
          style: {
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.overline.fontSize as string,
            fontWeight: "500",
          },
        },
        gridLineColor: theme.palette.border.secondary,
      },
      tooltip: {
        format:
          "<b>{key}</b><br/>{series.name}: {y}<br/>" +
          "Total: {point.stackTotal}",
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderWidth: 0,
          borderRadius: 4,
          dataLabels: {
            enabled: false,
          },
        },
      },
      series,
      legend: {
        enabled: series.length > 1,
        align: "center",
        verticalAlign: "bottom",
        itemStyle: {
          color: theme.palette.text.primary,
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.caption.fontSize as string,
        },
      },
    };
  }, [height, series, theme]);
};
